import { Button, Loader, UpliftThemeProvider } from '@uplift/ui-v2';
import { useQuery } from '@tanstack/react-query';
import { getProviderProfileFromUrl } from '@uplift/api';
import { useLocation } from 'react-router';
import { DateObject } from '@uplift/services';
import { useUpliftTimezone } from './helpers';
import { SelectedProvider, convertPublicProfileToSelectedProviderType } from '@uplift/pages';
import { Wave } from '../../components';
import { Box, Container, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { environment } from '../../../environments/environment';
import { PATIENT_ONBOARDING_ROUTING_CONFIG } from '@uplift/constants';
import { useStates } from '@uplift/queries';
import { useL10n } from '@uplift/hooks';
import { PublicProfilePayload } from '@uplift/types';
import { useTimezone } from '@uplift/contexts';

export type RouteParams = {
  medicalRole: string;
  providerData: string;
};

export const PublicProviderProfile: React.VFC = () => {
  const { classes } = useStyles();
  const location = useLocation();
  const l10n = useL10n();
  const timezone = useTimezone();
  const { states } = useStates();

  useUpliftTimezone();

  const { data, isLoading } = useQuery({
    queryKey: [ 'public-url', location.pathname ],
    queryFn: () => getProviderProfileFromUrl(location.pathname),
  });


  if (isLoading) {
    return (
      <UpliftThemeProvider>
        <Loader />
      </UpliftThemeProvider>
    );
  }

  const state = states.find(s => s.shortName === data.data.publicProfile.stateShortName);
  const provider = convertPublicProfileToSelectedProviderType(data.data, states);

  const onScheduleSession = (slot: DateObject) => {
    const toSend: PublicProfilePayload = {
      providerUrl: window.location.href,
      appointment: slot.utc().toISOString(),
      referrer: document.referrer,
      timezone
    };

    const encoded = window.btoa(JSON.stringify(toSend));

    window.location.href = `${environment.patientAppUrl}${PATIENT_ONBOARDING_ROUTING_CONFIG.onboarding}?profile_data=${encoded}`;
  };

  const findATherapistUrl = `${environment.patientAppUrl}${PATIENT_ONBOARDING_ROUTING_CONFIG.welcome}`;

  return (
    <UpliftThemeProvider>
      <Box className={classes.bg}>
        <SelectedProvider
          stateId={state?.id}
          selectedProvider={provider}
          onScheduleSession={onScheduleSession}
          isPublicProfile
          isScrollableView
        />
        <Wave>
          <Container className={classes.container} disableGutters>
            <Box maxWidth="555px" textAlign="center">
              <Typography variant="h3">
                {l10n.publicProfile.footer.header}
              </Typography>
              <Typography>
                <p>
                  {l10n.publicProfile.footer.line1}
                </p>
                <p>
                  {l10n.publicProfile.footer.line2}
                </p>
              </Typography>
              <Button variant="contained" color="secondary" component="a" href={findATherapistUrl} className={classes.button}>
                {l10n.publicProfile.footer.findATherapist}
              </Button>
            </Box>
          </Container>
        </Wave>
      </Box>
    </UpliftThemeProvider>
  );
};

const useStyles = makeStyles({ name: 'public-profile' })(theme => ({
  bg: {
    background: theme.palette.grey[50],
  },
  button: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 'auto',
    }
  },
  container: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(0, 2),
    [theme.breakpoints.up('sm')]: {
      maxWidth: '996px',
      marginLeft: 'auto',
      marginRight: 'auto',
    }
  }
}));
